import { Chip, IconButton, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAtomValue } from 'jotai/index';
import { FC, useMemo } from 'react';
import { MUIAvatar } from '../../../../components/Avatar/MUIAvatar';
import {
  IScenarioRunDataModel,
  IScenarioWeightedRunResponseDataModel,
} from '../../../../data-models/scenario-run.data-model';
import { IScenarioDataModel } from '../../../../data-models/scenario.data-model';
import { UnknownUser } from '../../../../data-models/user.data-model';
import { usersByEmailMapAtom } from '../../../../services/state/AppConfigStateJ';
import { formatDate } from '../../../../util/formatters/DateFormatters';
import { FMT } from '../../../../util/formatter-service';
import {
  ChipStack,
  listCardStyles,
  WeightedScenarioGrid,
  WeightedScenarioListItem,
} from '../components/commonStyledComponents';
import { getSimulationLabels } from '../utils/scenarioUtils';
import { useScenarioActions } from '../hooks/useScenarioActions';
import { ChanceCell } from './ChanceCell';
import { RelevanceTag } from './RelevanceTag';
import { ScenarioActions } from './ScenarioActions';
import { ScenarioTypes } from './ScenarioTypes';

const WeightedScenarioColumns = [
  'Scenario Name',
  '% Chance',
  'Expected Value',
  'Expected MOIC',
  'Simulated',
  'Created Date',
  'Owner',
  'Type',
  'Cap Table',
  '', // empty heading for the actions column
];

const ROW_PADDING = '0.5rem 0.75rem'; // 8px / 12px per design

const PLACEHOLDER = '—';

const expectedValueOf = (summary: IScenarioRunDataModel) =>
  FMT.format('currencyShort', summary.grossProceeds != null ? summary.grossProceeds : summary.fmv);
const expectedMoicOf = (summary: IScenarioRunDataModel) => FMT.format('multiplier', summary.grossMoic);

interface IWeightedScenarioListProps {
  scenarios: IScenarioDataModel[];
  weightedResult: IScenarioWeightedRunResponseDataModel | null;
  onWeightChange: (scenarioId: number, weight: number | null) => void;
  canEdit: boolean;
}

export const WeightedScenarioList: FC<IWeightedScenarioListProps> = ({
  scenarios,
  weightedResult,
  onWeightChange,
  canEdit,
}) => {
  const sortedScenarios = useMemo(() => {
    return [...scenarios].sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );
  }, [scenarios]);

  const summaryByScenarioId = useMemo(() => {
    const map = new Map<number, IScenarioRunDataModel>();
    weightedResult?.scenarios.forEach((row) => map.set(row.scenarioId, row.summary));
    return map;
  }, [weightedResult]);

  return (
    <WeightedScenarioGrid>
      <WeightedScenarioListItem style={{ padding: ROW_PADDING }}>
        {WeightedScenarioColumns.map((heading) => (
          <Typography variant='caption' key={heading || 'actions'} color='text.secondary'>
            {heading}
          </Typography>
        ))}
      </WeightedScenarioListItem>
      {sortedScenarios.map((scenario) => (
        <WeightedScenarioListItem key={scenario.id} sx={listCardStyles} style={{ padding: ROW_PADDING }}>
          <ScenarioItem
            scenario={scenario}
            summary={summaryByScenarioId.get(scenario.id)}
            onWeightChange={onWeightChange}
            canEdit={canEdit}
          />
        </WeightedScenarioListItem>
      ))}
      <WeightedOutcomeRow weightedResult={weightedResult} />
    </WeightedScenarioGrid>
  );
};

const ScenarioItem: FC<{
  scenario: IScenarioDataModel;
  summary?: IScenarioRunDataModel;
  onWeightChange: (scenarioId: number, weight: number | null) => void;
  canEdit: boolean;
}> = ({ scenario, summary, onWeightChange, canEdit }) => {
  const userMap = useAtomValue(usersByEmailMapAtom);
  const createdBy = userMap.get(scenario.createdBy!) ?? UnknownUser;

  return (
    <>
      <Typography noWrap variant='body2' justifyContent={'start'}>
        {scenario.name}
      </Typography>
      <ChanceCell
        scenarioId={scenario.id}
        value={scenario.probabilityWeight ?? null}
        onChange={onWeightChange}
        disabled={!canEdit}
      />
      <Typography variant='body2'>{summary ? expectedValueOf(summary) : PLACEHOLDER}</Typography>
      <Typography variant='body2'>{summary ? expectedMoicOf(summary) : PLACEHOLDER}</Typography>
      <ChipStack>
        {Array.from(getSimulationLabels(scenario))
          .sort()
          .map((label) => (
            <Chip key={label} label={label} />
          ))}
      </ChipStack>
      <Typography variant='body2'>{formatDate(scenario.createdAt)}</Typography>
      <Typography variant='body2' alignItems={'center'} display={'flex'} gap={'0.5rem'} component='div'>
        <MUIAvatar nameParts={createdBy.name?.split(' ') ?? []} src={null} />
        {createdBy.name}
      </Typography>
      <ScenarioTypes scenario={scenario} />
      <RelevanceTag outdated={scenario.outdated} />
      <ScenarioActions scenario={scenario} />
    </>
  );
};

const WeightedOutcomeRow: FC<{ weightedResult: IScenarioWeightedRunResponseDataModel | null }> = ({
  weightedResult,
}) => {
  const { gotoWeightedOutcome } = useScenarioActions();
  const outcome = weightedResult?.weightedOutcome;
  const enabled = Boolean(weightedResult);

  return (
    <WeightedScenarioListItem
      sx={{
        backgroundColor: 'action.hover',
        borderTop: ({ palette }) => `1px solid ${palette.divider}`,
        fontWeight: 600,
      }}
      style={{ padding: ROW_PADDING }}
    >
      <Typography variant='body2' fontWeight={600}>
        Weighted Outcome
      </Typography>
      <span /> {/* % Chance */}
      <Typography variant='body2'>{outcome ? expectedValueOf(outcome) : PLACEHOLDER}</Typography>
      <Typography variant='body2'>{outcome ? expectedMoicOf(outcome) : PLACEHOLDER}</Typography>
      <span /> {/* Simulated */}
      <span /> {/* Created Date */}
      <span /> {/* Owner */}
      <span /> {/* Type */}
      {outcome ? (
        <span /> /* Cap Table */
      ) : (
        <Typography variant='caption' color='text.secondary'>
          Available when allocation reaches 100%
        </Typography>
      )}
      <Tooltip title={enabled ? 'View weighted outcome' : 'Available when allocation reaches 100%'}>
        <span>
          <IconButton aria-label='view weighted outcome' disabled={!enabled} onClick={gotoWeightedOutcome}>
            <PlayArrowIcon fontSize='small' color={enabled ? 'secondary' : 'disabled'} />
          </IconButton>
        </span>
      </Tooltip>
    </WeightedScenarioListItem>
  );
};
